import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import useVendorAccessGate from "../../../../hooks/useVendorAccessGate";
import { useAuth } from "../../../../services/providers/authProvider";
import ricerca from "../../../../services/ricerca";
import clickService from "../../../../services/clickService";
import PageListEmptyState from "../../../Shared/pageListEmptyState";
import CardProdotto from "./cardProdotto";
import DettaglioProdotto from "./dettaglioProdotto";
import DialogClicks from "./dialogClicks";
import ProductCatalogFilters from "./ProductCatalogFilters";
import {
  CATALOG_VISIBILITY_FILTERS,
  mapCatalogVisibilityFilterToRequest,
} from "./productCatalogStatus";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VENDOR_PATHS } from "../../../../config/vendorNavPaths";

const PAGE_SIZE = 20;
const SUGGEST_LIMIT = 6;
const SUGGEST_MIN_CHARS = 3;
const SUGGEST_DEBOUNCE_MS = 480;
const PRICE_DEBOUNCE_MS = 400;
const PRICE_RANGE_EPSILON = 0.01;

function isCanceledRequest(error) {
  return (
    error?.name === "CanceledError"
    || error?.code === "ERR_CANCELED"
    || error?.message === "canceled"
  );
}

const NOME_COLONNA_PREZZO = "prezzo_di_vendita";

function isFullPriceRange(range, extent) {
  if (!Array.isArray(range) || range.length < 2 || !extent) {
    return true;
  }
  const [lo, hi] = range;
  return lo <= extent.min + PRICE_RANGE_EPSILON && hi >= extent.max - PRICE_RANGE_EPSILON;
}

function buildPriceFilterPayload(appliedPriceFilter) {
  if (!appliedPriceFilter) {
    return null;
  }
  const payload = {};
  const { min: lo, max: hi } = appliedPriceFilter;
  /* Backend rejects prezzoMin/prezzoMax <= 0; omit bound when zero (open range). */
  if (Number.isFinite(lo) && lo > 0) {
    payload.prezzoMin = lo;
  }
  if (Number.isFinite(hi) && hi > 0) {
    payload.prezzoMax = hi;
  }
  return Object.keys(payload).length > 0 ? payload : null;
}

function mapStoricoToRows(storici, catalogId) {
  if (!Array.isArray(storici) || storici.length === 0) return [];
  const fmt = new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return [...storici]
    .map((s) => {
      const d = s.dataOraClick ? new Date(s.dataOraClick) : null;
      const ts = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
      return {
        _ts: ts,
        dateLabel: ts ? fmt.format(d) : "",
        browser: s.browser ?? "-",
        ip: s.indirizzoIp ?? "-",
        catalogId: catalogId ?? "-",
      };
    })
    .sort((a, b) => b._ts - a._ts)
    .slice(0, 20)
    .map(({ _ts, ...row }) => row);
}

const Prodotti = () => {
  useVendorAccessGate();
  const { vendorId, token } = useAuth() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const catalogQueryKey = VENDOR_PATHS.PRODOTTI_CATALOG_QUERY;

  const [products, setProducts] = useState([]);
  const [totalsResults, setTotalsResults] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorState, setErrorState] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSuggest, setDebouncedSuggest] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  /** Only price sort: asc = crescente (default), desc = decrescente */
  const [sortOrder, setSortOrder] = useState("asc");
  const [visibilityFilter, setVisibilityFilter] = useState(CATALOG_VISIBILITY_FILTERS.ALL);

  const [priceRange, setPriceRange] = useState([0, 1]);
  const [sliderExtent, setSliderExtent] = useState({ min: 0, max: 1 });
  /** null = no price filter; otherwise active min/max sent to API until reset. */
  const [appliedPriceFilter, setAppliedPriceFilter] = useState(null);
  const priceRangeRef = useRef([0, 1]);
  const sliderExtentRef = useRef({ min: 0, max: 1 });
  const priceChangePendingRef = useRef(false);

  useEffect(() => {
    priceRangeRef.current = priceRange;
  }, [priceRange]);

  useEffect(() => {
    sliderExtentRef.current = sliderExtent;
  }, [sliderExtent]);

  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOptions, setSuggestOptions] = useState([]);
  const suggestAbortRef = useRef(null);

  const [nextPage, setNextPage] = useState(0);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailClickTotal, setDetailClickTotal] = useState(null);
  const [detailClicksLoading, setDetailClicksLoading] = useState(false);
  const [clicksDialogOpen, setClicksDialogOpen] = useState(false);
  const [clicksDialogRows, setClicksDialogRows] = useState([]);
  const [clicksDialogLoading, setClicksDialogLoading] = useState(false);

  const searchWrapRef = useRef(null);

  const canQueryProducts =
    vendorId != null && vendorId !== "" && Number.isFinite(Number(vendorId));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSuggest(searchInput.trim()), SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    priceChangePendingRef.current = false;
    setAppliedPriceFilter(null);
  }, [committedSearch, visibilityFilter, sortOrder]);

  const resolveAppliedPriceFilter = useCallback((range, extent = sliderExtentRef.current) => {
    if (isFullPriceRange(range, extent)) {
      return null;
    }
    return { min: range[0], max: range[1] };
  }, []);

  const applyPriceFilterSelection = useCallback((range) => {
    priceChangePendingRef.current = false;
    setPriceRange(range);
    setAppliedPriceFilter(resolveAppliedPriceFilter(range));
  }, [resolveAppliedPriceFilter]);

  /* Suggerimenti: non legati all’ordinamento scelto (evita ricerche inutili). Parametri fissi leggeri come default prezzo ASC. */
  useEffect(() => {
    suggestAbortRef.current?.abort();
    suggestAbortRef.current = null;

    if (debouncedSuggest.length < SUGGEST_MIN_CHARS) {
      setSuggestOptions([]);
      setSuggestLoading(false);
      return undefined;
    }
    if (!token || !canQueryProducts) return undefined;

    const ac = new AbortController();
    suggestAbortRef.current = ac;
    let cancelled = false;

    (async () => {
      setSuggestLoading(true);
      try {
        const response = await ricerca.products(
          {
            isFromVendor: true,
            limit: SUGGEST_LIMIT,
            page: 0,
            vendorId: [Number(vendorId)],
            ordine: "ASC",
            nomeColonna: NOME_COLONNA_PREZZO,
            ricerca: debouncedSuggest,
            ruolo: "vendor",
          },
          {
            signal: ac.signal,
            skipGlobalErrorDispatch: true,
          }
        );
        if (cancelled) return;
        const body = response?.data ?? {};
        const list = Array.isArray(body.products) ? body.products : [];
        setSuggestOptions(list);
      } catch (e) {
        if (cancelled || isCanceledRequest(e)) return;
        setSuggestOptions([]);
      } finally {
        if (!cancelled) {
          setSuggestLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
      if (suggestAbortRef.current === ac) {
        suggestAbortRef.current = null;
      }
    };
  }, [debouncedSuggest, token, canQueryProducts, vendorId]);

  const applyBoundsFromResponse = useCallback((body) => {
    let mn = Number(body?.minPrezzo);
    let mx = Number(body?.maxPrezzo);
    if (!Number.isFinite(mn)) mn = 0;
    if (!Number.isFinite(mx)) mx = mn;
    const maxClamped = mx > mn ? mx : mn + 0.01;
    sliderExtentRef.current = { min: mn, max: maxClamped };
    setSliderExtent({ min: mn, max: maxClamped });
    const next = [mn, maxClamped];
    priceRangeRef.current = next;
    setPriceRange(next);
  }, []);

  const runProductsFetch = useCallback(
    async ({ page, append }) => {
      if (!token || !canQueryProducts) return null;
      const loadingSetter = append ? setIsLoadingMore : setIsLoading;
      loadingSetter(true);
      setErrorState("");
      const priceFilterPayload = buildPriceFilterPayload(appliedPriceFilter);
      const applyPriceFilter = priceFilterPayload != null;
      try {
        const data = {
          isFromVendor: true,
          limit: PAGE_SIZE,
          page,
          vendorId: [Number(vendorId)],
          ordine: sortOrder === "desc" ? "DESC" : "ASC",
          nomeColonna: NOME_COLONNA_PREZZO,
          ruolo: "vendor",
          ...mapCatalogVisibilityFilterToRequest(visibilityFilter),
        };
        if (committedSearch) {
          data.ricerca = committedSearch;
        }
        if (priceFilterPayload) {
          Object.assign(data, priceFilterPayload);
        }
        const response = await ricerca.products(data);
        const body = response?.data ?? {};
        const list = Array.isArray(body.products) ? body.products : [];
        const total = Number(body.totalsResults ?? 0);

        if (!applyPriceFilter) {
          applyBoundsFromResponse(body);
        }

        if (append) {
          setProducts((prev) => {
            const seen = new Set(prev.map((p) => p.catalogListId ?? p.productListId));
            const merged = [...prev];
            for (const p of list) {
              const id = p.catalogListId ?? p.productListId;
              if (id != null && !seen.has(id)) {
                seen.add(id);
                merged.push(p);
              }
            }
            return merged;
          });
        } else {
          setProducts(list);
        }
        setTotalsResults(total);
        setNextPage(page + 1);
        return { list, total };
      } catch (e) {
        console.error("Errore nel caricamento prodotti:", e);
        setErrorState("Impossibile caricare i prodotti.");
        if (!append) {
          setProducts([]);
          setTotalsResults(0);
        }
        return null;
      } finally {
        loadingSetter(false);
      }
    },
    [token, canQueryProducts, vendorId, sortOrder, committedSearch, visibilityFilter, appliedPriceFilter, applyBoundsFromResponse]
  );

  useEffect(() => {
    if (!token || !canQueryProducts) return;
    setNextPage(0);
    runProductsFetch({ page: 0, append: false });
  }, [token, canQueryProducts, committedSearch, sortOrder, visibilityFilter, appliedPriceFilter, runProductsFetch]);

  useEffect(() => {
    if (!priceChangePendingRef.current) return;
    const t = setTimeout(() => {
      if (!priceChangePendingRef.current) return;
      priceChangePendingRef.current = false;
      setAppliedPriceFilter(resolveAppliedPriceFilter(priceRangeRef.current));
    }, PRICE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [priceRange, resolveAppliedPriceFilter]);

  const hasMore = products.length < totalsResults;

  const catalogIdFromUrl = useMemo(() => {
    const raw = searchParams.get(catalogQueryKey);
    if (raw == null || raw === "") return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }, [searchParams, catalogQueryKey]);

  useEffect(() => {
    const raw = searchParams.get(catalogQueryKey);
    if (raw != null && raw !== "" && !Number.isFinite(Number(raw))) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(catalogQueryKey);
        return next;
      }, { replace: true });
      return;
    }
    if (catalogIdFromUrl == null) {
      setSelectedProduct(null);
      setDetailClickTotal(null);
      setDetailClicksLoading(false);
      return;
    }
    const found = products.find((p) => Number(p.catalogListId ?? p.productListId) === catalogIdFromUrl);
    if (found) {
      setSelectedProduct((prev) => {
        const prevId = prev ? Number(prev.catalogListId ?? prev.productListId) : null;
        return prevId === catalogIdFromUrl ? prev : found;
      });
      return;
    }
    if (products.length > 0) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(catalogQueryKey);
        return next;
      }, { replace: true });
    }
  }, [catalogIdFromUrl, products, catalogQueryKey, setSearchParams, searchParams]);

  const handleOpenDetail = (product) => {
    const id = product?.catalogListId ?? product?.productListId;
    if (id == null || !Number.isFinite(Number(id))) return;
    setSearchParams({ [catalogQueryKey]: String(id) });
  };

  const handleBackToList = useCallback(() => {
    if (!searchParams.get(catalogQueryKey)) {
      setSelectedProduct(null);
      setDetailClickTotal(null);
      setDetailClicksLoading(false);
      return;
    }
    const idx = globalThis.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(catalogQueryKey);
      return next;
    }, { replace: true });
  }, [navigate, searchParams, setSearchParams, catalogQueryKey]);

  useEffect(() => {
    if (!selectedProduct || !token || !canQueryProducts) {
      return;
    }
    const catalogListId = selectedProduct.catalogListId ?? selectedProduct.productListId;
    if (catalogListId == null || !Number.isFinite(Number(catalogListId))) {
      setDetailClickTotal(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailClicksLoading(true);
      setDetailClickTotal(null);
      try {
        const res = await clickService.fetchCatalogClickStats({
          catalogListId: Number(catalogListId),
          nome: selectedProduct.nome ?? "",
        });
        const body = res?.data ?? {};
        if (cancelled) return;
        if (body.statusCode === 202 && body.numClickGenerati != null) {
          setDetailClickTotal(Number(body.numClickGenerati));
        } else {
          setDetailClickTotal(0);
        }
      } catch {
        if (!cancelled) setDetailClickTotal(0);
      } finally {
        if (!cancelled) setDetailClicksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProduct, token, canQueryProducts]);

  const handleOpenClicksDialog = async () => {
    if (!selectedProduct || !vendorId) return;
    const catalogListId = selectedProduct.catalogListId ?? selectedProduct.productListId;
    if (catalogListId == null) return;
    setClicksDialogOpen(true);
    setClicksDialogLoading(true);
    setClicksDialogRows([]);
    try {
      const res = await clickService.storici({
        vendorListId: Number(vendorId),
        catalogoId: Number(catalogListId),
        startDate: null,
        endDate: null,
      });
      const body = res?.data ?? {};
      const storici = body.storici ?? body.Storici ?? [];
      setClicksDialogRows(mapStoricoToRows(storici, catalogListId));
    } catch (e) {
      console.error(e);
      setClicksDialogRows([]);
    } finally {
      setClicksDialogLoading(false);
    }
  };

  const handleCloseClicks = () => {
    setClicksDialogOpen(false);
    setClicksDialogRows([]);
  };

  const applySearchSubmit = () => {
    const q = searchInput.trim();
    setCommittedSearch(q);
    setSuggestOpen(false);
  };

  const resetSearch = () => {
    suggestAbortRef.current?.abort();
    suggestAbortRef.current = null;
    setSearchInput("");
    setCommittedSearch("");
    setDebouncedSuggest("");
    setSuggestOptions([]);
    setSuggestLoading(false);
    setSuggestOpen(false);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const sliderDisabled = sliderExtent.max <= sliderExtent.min + 0.0001;

  if (!canQueryProducts) {
    return (
      <Typography sx={{ fontFamily: "Rubik", color: "#808080" }}>
        Nessun vendor associato all&apos;utente corrente.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
        <Grid2 container spacing={2} alignItems="center" sx={{ mb: selectedProduct ? 2 : 3 }}>
          <Grid2 size={{ xs: 12, md: selectedProduct ? 12 : 4 }}>
            <Box
              sx={{
                textAlign: "left",
                pl: { md: selectedProduct ? 0 : 1 },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Rubik",
                  fontWeight: "bold",
                  color: "#0b3d87",
                }}
              >
                Catalogo / Prodotti
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  fontFamily: "Rubik",
                  color: "#808080",
                }}
              >
                Visualizza i prodotti del tuo catalogo.
              </Typography>
            </Box>
          </Grid2>
          {!selectedProduct && (
            <Grid2 size={{ xs: 12, md: 8 }}>
              <ProductCatalogFilters
                searchWrapRef={searchWrapRef}
                searchInput={searchInput}
                onSearchInputChange={(e) => {
                  setSearchInput(e.target.value);
                  setSuggestOpen(true);
                }}
                onSearchFocus={() => setSuggestOpen(true)}
                onSearchKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applySearchSubmit();
                  }
                }}
                onSearchReset={resetSearch}
                onSearchSubmit={applySearchSubmit}
                searchPending={Boolean(searchInput.trim()) && searchInput.trim() !== committedSearch}
                searchCommitted={Boolean(committedSearch) && searchInput.trim() === committedSearch}
                suggestLoading={suggestLoading}
                suggestOpen={suggestOpen}
                suggestMinChars={SUGGEST_MIN_CHARS}
                suggestOptions={suggestOptions}
                onSuggestSelect={(p) => {
                  const q = (p.nome ?? "").trim();
                  setSearchInput(q);
                  setCommittedSearch(q);
                  setSuggestOpen(false);
                }}
                sortOrder={sortOrder}
                onSortOrderChange={setSortOrder}
                visibilityFilter={visibilityFilter}
                onVisibilityFilterChange={setVisibilityFilter}
                priceRange={priceRange}
                onPriceRangeChange={(v) => {
                  priceChangePendingRef.current = true;
                  setPriceRange(v);
                }}
                onPriceRangeCommit={applyPriceFilterSelection}
                sliderExtent={sliderExtent}
                sliderDisabled={sliderDisabled}
                isLoading={isLoading}
              />
            </Grid2>
          )}
        </Grid2>

        {selectedProduct ? (
          <DettaglioProdotto
            product={selectedProduct}
            onBack={handleBackToList}
            totalClicks={detailClickTotal}
            clicksLoading={detailClicksLoading}
            onOpenClicksDialog={handleOpenClicksDialog}
          />
        ) : (
          <Box mt={0}>
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : errorState ? (
                <Typography sx={{ fontFamily: "Rubik", color: "#e00036", fontWeight: "bold" }}>
                  {errorState}
                </Typography>
              ) : products.length === 0 ? (
                <PageListEmptyState message="Nessun prodotto trovato 🫤" />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {products.map((p) => (
                    <CardProdotto
                      key={p.catalogListId ?? p.productListId}
                      product={p}
                      onOpenDetail={() => handleOpenDetail(p)}
                    />
                  ))}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      disabled={!hasMore || isLoadingMore}
                      onClick={() => {
                        if (hasMore && !isLoadingMore) {
                          runProductsFetch({ page: nextPage, append: true });
                        }
                      }}
                      sx={{
                        fontFamily: "Rubik",
                        textTransform: "none",
                        borderRadius: "999px",
                        px: 3,
                        fontWeight: "bold",
                        borderColor: "#0b3d87",
                        color: "#0b3d87",
                        "&:hover": { borderColor: "#092b6a", backgroundColor: "#e8f0ff" },
                      }}
                    >
                      {isLoadingMore ? (
                        <CircularProgress size={22} sx={{ color: "#0b3d87" }} />
                      ) : !hasMore ? (
                        "Non ci sono altri risultati"
                      ) : (
                        "Carica altro"
                      )}
                    </Button>
                    {totalsResults > 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "Rubik", color: "#808080", fontSize: 13 }}
                      >
                        {products.length} di {totalsResults} prodotti
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
          </Box>
        )}
      <DialogClicks open={clicksDialogOpen} onClose={handleCloseClicks} clicks={clicksDialogRows} loading={clicksDialogLoading} />
    </Box>
  );
};

export default Prodotti;
